import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Calendar, CheckCircle2, AlertCircle, Clock, Download, Upload, Plus, X, Receipt, TrendingUp } from 'lucide-react';

const fC = (v) => {
  if (!v || v === 0) return '₹0';
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n.toFixed(0)}`;
};

// Generate last 6 months + current + next as options
function monthOptions() {
  const out = [];
  const now = new Date();
  for (let i = 6; i >= -1; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

export default function MonthlyBillingTab({ outletName, divisionId, onChange }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState({ fixed_component: '', variable_component: '', invoice_number: '', po_number: '', notes: '' });

  // Caller passes either outletName (single-outlet view) or divisionId (aggregate across division).
  const scopeParam = outletName
    ? `outlet=${encodeURIComponent(outletName)}`
    : `division_id=${encodeURIComponent(divisionId || '')}`;

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/b2b-revenue/monthly-billing?${scopeParam}&month=${month}`);
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [outletName, divisionId, month]);

  const startBill = (row) => {
    setEditingRow(row);
    setForm({
      fixed_component: row.fixed_actual > 0 ? String(row.fixed_actual) : String(row.fixed_expected || ''),
      variable_component: String(row.variable_actual || ''),
      invoice_number: '', po_number: '', notes: '',
    });
  };

  const submitBill = async () => {
    if (!editingRow) return;
    const fixed = parseFloat(form.fixed_component || 0);
    const variable = parseFloat(form.variable_component || 0);
    if (fixed + variable <= 0) return alert('Enter a fixed or variable amount');
    try {
      await api.post('/b2b-revenue', {
        outlet: outletName || editingRow.outlet || '',
        deal_id: editingRow.deal_id,
        client_name: editingRow.client_name,
        deal_name: editingRow.client_name,
        month,
        amount: fixed + variable,
        fixed_component: fixed,
        variable_component: variable,
        invoice_number: form.invoice_number,
        po_number: form.po_number,
        notes: form.notes,
        entry_type: 'realised',
      });
      setEditingRow(null);
      refresh();
      if (onChange) onChange();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
  };

  if (loading) return <div className="text-center text-xs py-8" style={{ color: '#9CA3AF' }}>Loading monthly billing...</div>;
  if (!data) return null;

  const { rows, totals } = data;
  const billedPct = rows.length > 0 ? (totals.clients_billed / rows.length) * 100 : 0;

  return (
    <div className="space-y-4" data-testid="monthly-billing-tab">
      {/* Header */}
      <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: '#1B2A4A' }}>
              <Receipt size={14} /> Monthly Billing
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>Per-client fixed retainer + variable ops revenue for a given month.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Month</label>
            <select value={month} onChange={e => setMonth(e.target.value)} className="px-3 py-1.5 rounded-md border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="billing-month-select">
              {monthOptions().map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3">
          <SummaryCard label="Fixed Expected" value={fC(totals.fixed_expected)} accent="#BA7517" bg="#FFFBEB" sub={`${rows.length} clients`} />
          <SummaryCard label="Fixed Realised" value={fC(totals.fixed_actual)} accent="#2D8B5E" bg="#ECFDF5" sub={`${totals.clients_billed}/${rows.length} billed`} />
          <SummaryCard label="Variable Ops" value={fC(totals.variable_actual)} accent="#378ADD" bg="#EFF6FF" sub="Pettycash, logistics, ad-hoc" />
          <SummaryCard label="Total This Month" value={fC(totals.total_actual)} accent="#1B2A4A" bg="#F7F5F2" sub={`${billedPct.toFixed(0)}% of clients billed`} bold />
        </div>

        <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
          <div className="h-full transition-all" style={{ width: `${billedPct}%`, backgroundColor: billedPct >= 80 ? '#2D8B5E' : billedPct >= 50 ? '#BA7517' : '#D4726A' }} />
        </div>
        <div className="flex justify-between text-[10px] mt-1" style={{ color: '#6B7280' }}>
          <span>{totals.clients_billed} billed · {totals.clients_partial} partial · {totals.clients_pending} pending</span>
          <span>{fC(totals.total_actual)} / {fC(totals.fixed_expected)} expected</span>
        </div>
      </div>

      {/* Per-client table */}
      <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }} data-testid="billing-rows">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead style={{ backgroundColor: '#F7F5F2' }}>
              <tr>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Client</th>
                <th className="text-left px-3 py-2 font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Service</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Fixed Expected</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Fixed Billed</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Variable</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Total</th>
                <th className="text-center px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {rows.map(r => {
                const status = r.status === 'billed' ? { label: 'Billed', icon: CheckCircle2, color: '#2D8B5E', bg: '#ECFDF5' }
                  : r.status === 'partial' ? { label: 'Partial', icon: Clock, color: '#BA7517', bg: '#FFFBEB' }
                  : { label: 'Pending', icon: AlertCircle, color: '#D4726A', bg: '#FEF2F2' };
                const Icon = status.icon;
                return (
                  <tr key={r.deal_id} className="hover:bg-gray-50" data-testid={`billing-row-${r.deal_id}`}>
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>
                      {r.client_name}
                      {r.stage === 'Pending Renewal' && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#BA7517' }}>RENEWAL</span>}
                    </td>
                    <td className="px-3 py-2 truncate max-w-[140px] hidden sm:table-cell" style={{ color: '#6B7280' }}>{r.entity || '—'}</td>
                    <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{fC(r.fixed_expected)}</td>
                    <td className="px-3 py-2 text-right font-semibold" style={{ color: r.fixed_actual > 0 ? '#2D8B5E' : '#9CA3AF' }}>{fC(r.fixed_actual)}</td>
                    <td className="px-3 py-2 text-right" style={{ color: r.variable_actual > 0 ? '#378ADD' : '#9CA3AF' }}>{fC(r.variable_actual)}</td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: '#1B2A4A' }}>{fC(r.total_actual)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: status.bg, color: status.color }}>
                        <Icon size={10} /> {status.label}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => startBill(r)} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-md text-white hover:opacity-90 whitespace-nowrap" style={{ backgroundColor: r.total_actual > 0 ? '#6B7280' : '#2A7B88' }} data-testid={`bill-btn-${r.deal_id}`}>
                        {r.total_actual > 0 ? 'Edit Revenue' : '+ Record Revenue'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-xs" style={{ color: '#9CA3AF' }}>No active clients in this outlet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bill / edit modal */}
      {editingRow && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3" onClick={() => setEditingRow(null)}>
          <div className="bg-white rounded-t-xl sm:rounded-xl border max-w-lg w-full p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} onClick={e => e.stopPropagation()} data-testid="bill-form">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{editingRow.total_actual > 0 ? 'Edit' : 'Record'} Billing — {editingRow.client_name}</p>
                <p className="text-[10px]" style={{ color: '#6B7280' }}>{editingRow.entity} · {month}</p>
              </div>
              <button onClick={() => setEditingRow(null)}><X size={16} style={{ color: '#6B7280' }} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Fixed Retainer ₹ (Expected {fC(editingRow.fixed_expected)})</label>
                <input type="number" value={form.fixed_component} onChange={e => setForm(f => ({ ...f, fixed_component: e.target.value }))} placeholder={String(Math.round(editingRow.fixed_expected))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="bill-fixed" />
                {form.fixed_component && <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>= {fC(parseFloat(form.fixed_component) || 0)}</p>}
              </div>
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Variable Ops ₹ (pettycash / logistics / ad-hoc)</label>
                <input type="number" value={form.variable_component} onChange={e => setForm(f => ({ ...f, variable_component: e.target.value }))} placeholder="0" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="bill-variable" />
                {form.variable_component && <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>= {fC(parseFloat(form.variable_component) || 0)}</p>}
              </div>
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Invoice #</label>
                <input value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="INV-2026-001" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="bill-invoice" />
              </div>
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>PO # (if applicable)</label>
                <input value={form.po_number} onChange={e => setForm(f => ({ ...f, po_number: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="bill-po" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Notes</label>
                <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Pettycash float + April logistics" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="bill-notes" />
              </div>
            </div>
            <div className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
              <span className="text-[11px]" style={{ color: '#6B7280' }}>Total for {editingRow.client_name} ({month}):</span>
              <span className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{fC((parseFloat(form.fixed_component) || 0) + (parseFloat(form.variable_component) || 0))}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={submitBill} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="bill-submit">Record Billing</button>
              <button onClick={() => setEditingRow(null)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent, bg, sub, bold }) {
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: bg }}>
      <div className="flex items-center gap-1 mb-1">
        <TrendingUp size={10} style={{ color: accent }} />
        <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: accent }}>{label}</p>
      </div>
      <p className={`${bold ? 'text-xl font-bold' : 'text-lg font-semibold'}`} style={{ color: '#1B2A4A' }}>{value}</p>
      {sub && <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>{sub}</p>}
    </div>
  );
}
